<template>
  <f7-page data-tour-id="samples-register-template-details-page">
    <div v-if="loadingSamplingTemplate" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader />
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div
      v-if="samplingTemplate"
      class="main-offer-card"
      data-tour-id="samples-register-template-details-container"
    >
      <TemplateDetailsSummary
        class="margin-bottom"
        :title="templateName"
        :description-label="descriptionText"
        :description="templateDescription"
        :status-label="statusText"
        :status="templateStatus"
        :created-by-label="createdByText"
        :created-by="templateCreatedBy"
        :budget-label="budgetText"
        :budget="templateBudget"
        :show-budget="showTemplateBudgetSummary"
        :updated-at-label="updatedAtText"
        :updated-at="templateUpdatedAt"
        @edit="onEditTemplate"
        @register-template="onRegisterTemplateClick"
      />

      <TemplateSamplesDetails
        :samples="templateSamples"
        :editable="true"
        :saving="savingTemplateSamples"
        :budget-code="templateBudgetCode"
        :show-budget-column="hasMultipleTemplateBudgets"
        @samples-updated="onTemplateSamplesUpdated"
      />
    </div>
  </f7-page>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  NOTIFY_TIME_LONG,
  SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY,
} from '@/js/constants';
import TemplateSamplesDetails from '@/components/TemplatesManager/TemplateSamplesDetails/index.vue';
import TemplateDetailsSummary from '@/components/TemplatesManager/TemplateDetailsSummary/index.vue';
import {
  consumePendingSamplesRegisterTemplateDetailsTourStart,
  startSamplesRegisterTemplateDetailsGuidedTour,
  stopSamplesRegisterGuidedTour,
} from '@/services/samplesRegisterGuidedTour';
import { emitSamplesRegisterGa4Event } from '@/services/samplesRegisterAnalytics';

export default {
  name: 'TemplateDetailsPage',
  components: {
    TemplateSamplesDetails,
    TemplateDetailsSummary,
  },
  data() {
    return {
      savingTemplateSamples: false,
      loaded: false,
      guidedTourStartTimeoutId: null,
    };
  },
  computed: {
    ...mapState('contact', ['company']),
    ...mapState('Offers', ['detallesPresupuestos']),
    ...mapState('samplingTemplatesModule', [
      'samplingTemplate',
      'loadingSamplingTemplate',
    ]),
    ...mapState('samplesRegisterModule', [
      'idInvoice',
      'invoiceCodeInfoPopUp',
    ]),
    templateData() {
      return this.samplingTemplate?.template || {};
    },
    templateId() {
      return (
        this.templateData?.id || this.samplingTemplate?.id || null
      );
    },
    templateSamples() {
      return this.samplingTemplate?.samples || [];
    },
    templateSampleBudgetCodes() {
      return [
        ...new Set(
          this.templateSamples
            .map((sample) => this.getTemplateSampleBudgetCode(sample))
            .filter(Boolean)
        ),
      ];
    },
    hasMultipleTemplateBudgets() {
      return this.templateSampleBudgetCodes.length > 1;
    },
    showTemplateBudgetSummary() {
      return !this.hasMultipleTemplateBudgets;
    },
    templateBudget() {
      if (this.hasMultipleTemplateBudgets) return '-';
      const budget = this.samplingTemplate?.defaults?.codPresupuesto;

      if (!budget) return '-';

      if (typeof budget === 'object') {
        const budgetCode =
          budget.id ||
          budget.codigo ||
          budget.codPresupuesto ||
          budget.CodPresupuesto ||
          null;
        const budgetDescription =
          budget.descripcion || budget.Descripcion || null;

        if (budgetCode && budgetDescription) {
          return `${budgetCode} - ${budgetDescription}`;
        }

        return budgetDescription || budgetCode || '-';
      }

      return String(budget);
    },
    templateBudgetCode() {
      if (this.hasMultipleTemplateBudgets) return null;
      const budget = this.samplingTemplate?.defaults?.codPresupuesto;
      if (!budget) return null;

      if (typeof budget === 'object') {
        return (
          budget.id ||
          budget.codigo ||
          budget.codPresupuesto ||
          budget.CodPresupuesto ||
          null
        );
      }

      return budget;
    },
    templateName() {
      return this.templateData.name || this.templateTitleFallback;
    },
    templateDescription() {
      return this.templateData.shortDescription || '-';
    },
    templateStatus() {
      return this.templateData.active
        ? this.activeText
        : this.inactiveText;
    },
    templateCreatedBy() {
      return this.samplingTemplate?.audit?.createdBy?.name || '-';
    },
    templateUpdatedAt() {
      const updatedAt = this.samplingTemplate?.audit?.updatedAt;
      if (!updatedAt) return '-';

      return this.formatTemplateDate(updatedAt);
    },
    templateTitleFallback() {
      return this.$t('TemplatesListTitle') !== 'TemplatesListTitle'
        ? this.$t('TemplatesListTitle')
        : 'Detalle de plantilla';
    },
    descriptionText() {
      return this.$t('description') !== 'description'
        ? this.$t('description')
        : 'Descripcion';
    },
    statusText() {
      return this.$t('status') !== 'status'
        ? this.$t('status')
        : 'Estado';
    },
    budgetText() {
      return this.$t('budget') !== 'budget'
        ? this.$t('budget')
        : 'Presupuesto';
    },
    createdByText() {
      return this.$t('createdBy') !== 'createdBy'
        ? this.$t('createdBy')
        : 'Creado por';
    },
    updatedAtText() {
      return this.$t('lastUpdated') !== 'lastUpdated'
        ? this.$t('lastUpdated')
        : 'Ultima modificacion';
    },
    activeText() {
      return this.$t('active') !== 'active'
        ? this.$t('active')
        : 'Activa';
    },
    inactiveText() {
      return this.$t('inactive') !== 'inactive'
        ? this.$t('inactive')
        : 'Inactiva';
    },
  },
  beforeMount() {
    this.setCurrentRoute([
      {
        name: this.$t('TemplatesListTitle'),
        disabled: false,
        page: 'TemplatesListPage',
        active: false,
      },
      {
        name: this.templateName,
        disabled: false,
        page: 'TemplateDetailsPage',
        active: true,
      },
    ]);
  },
  mounted() {
    if (!this.samplingTemplate && !this.loadingSamplingTemplate) {
      this.$nextTick(() => {
        try {
          this.$f7router.navigate(
            {
              name: 'TemplatesListPage',
            },
            {
              animate: false,
              replaceState: true,
              reloadCurrent: true,
            }
          );
        } catch (error) {
          this.$f7router.navigate(
            '/TemplatesManager/TemplatesList/',
            {
              animate: false,
              replaceState: true,
              reloadCurrent: true,
            }
          );
        }
      });
    }
    this.loaded = true;
    this.scheduleSamplesGuidedTourStart();
  },
  beforeDestroy() {
    this.clearSamplesGuidedTourStartTimeout();
    stopSamplesRegisterGuidedTour(this);
  },
  methods: {
    clearSamplesGuidedTourStartTimeout() {
      if (this.guidedTourStartTimeoutId) {
        clearTimeout(this.guidedTourStartTimeoutId);
        this.guidedTourStartTimeoutId = null;
      }
    },
    scheduleSamplesGuidedTourStart({ force = false } = {}) {
      if (!this.loaded) return;
      const shouldContinueFromTemplatesList =
        force ||
        consumePendingSamplesRegisterTemplateDetailsTourStart();
      if (!shouldContinueFromTemplatesList) {
        return;
      }

      this.clearSamplesGuidedTourStartTimeout();
      this.guidedTourStartTimeoutId = setTimeout(() => {
        this.guidedTourStartTimeoutId = null;
        startSamplesRegisterTemplateDetailsGuidedTour(this, {
          force: true,
        });
      }, SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY);
    },
    openTourSamplesRegister() {
      this.$f7router.navigate(
        {
          name: 'SamplesRegister',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
      return true;
    },
    toBoolean(value, fallback = false) {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return (
          normalized === 'true' ||
          normalized === '1' ||
          normalized === 'si' ||
          normalized === 'yes'
        );
      }
      return fallback;
    },
    normalizeString(value) {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    getTemplateAnalyticsParams(extraParams = {}) {
      return {
        template_id: this.templateId || undefined,
        template_name:
          this.normalizeString(this.templateData?.name) || undefined,
        template_active: Boolean(this.templateData?.active),
        samples_count: Array.isArray(this.templateSamples)
          ? this.templateSamples.length
          : 0,
        budget_code:
          this.normalizeString(this.templateBudgetCode) || undefined,
        ...extraParams,
      };
    },
    normalizeTemplateText(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : null;
      }
      return value;
    },
    toNullableNumber(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    getCurrentInvoiceId() {
      if (Array.isArray(this.idInvoice)) {
        return this.idInvoice[0] || null;
      }
      return this.idInvoice || null;
    },
    getCurrentInvoiceCode() {
      if (Array.isArray(this.invoiceCodeInfoPopUp)) {
        return this.invoiceCodeInfoPopUp[0] || null;
      }
      return this.invoiceCodeInfoPopUp || null;
    },
    getTemplateBudgetCode(templateData) {
      const budget =
        templateData?.defaults?.codPresupuesto ||
        templateData?.defaults?.CodPresupuesto ||
        null;

      if (!budget) return '';

      if (typeof budget === 'object') {
        return this.normalizeString(
          budget.id ||
            budget.codigo ||
            budget.codPresupuesto ||
            budget.CodPresupuesto
        );
      }

      return this.normalizeString(budget);
    },
    getTemplateSamples(templateData) {
      return Array.isArray(templateData?.samples)
        ? templateData.samples
        : [];
    },
    getTemplateSampleDescription(sample) {
      return this.normalizeString(
        sample?.descripcionMuestra || sample?.DescripcionMuestra
      );
    },
    getTemplateSampleTypeId(sample) {
      return (
        sample?.tipoMuestra?.id ||
        sample?.idTipoMuestra ||
        sample?.Descripcion?.idTipoMuestra ||
        sample?.Descripcion ||
        null
      );
    },
    getTemplateSampleFamilyId(sample) {
      return (
        sample?.familiaTM?.id ||
        sample?.idFamilia ||
        sample?.FamiliaTM ||
        null
      );
    },
    getTemplateSampleArticleId(sample) {
      return this.normalizeString(
        sample?.articulo?.id ||
          sample?.CodArticulo ||
          sample?.codArticulo ||
          sample?.Articulo
      );
    },
    getTemplateSampleBudgetCode(sample) {
      const budget =
        sample?.codPresupuesto || sample?.CodPresupuesto || null;
      if (!budget) return '';

      if (typeof budget === 'object') {
        return this.normalizeString(
          budget?.id ||
            budget?.codigo ||
            budget?.codPresupuesto ||
            budget?.CodPresupuesto
        );
      }

      return this.normalizeString(budget);
    },
    findOfferLineForSample(sample, offerLines = []) {
      const articleId = this.getTemplateSampleArticleId(sample);
      if (!articleId) return null;

      const candidates = offerLines.filter(
        (line) =>
          this.normalizeString(line?.CodArticulo) === articleId
      );
      if (!candidates.length) return null;

      const familyId = this.toNullableNumber(
        this.getTemplateSampleFamilyId(sample)
      );
      if (familyId == null) return candidates[0];

      const byFamily = candidates.find(
        (line) => this.toNullableNumber(line?.IdFamilia) === familyId
      );
      return byFamily || candidates[0];
    },
    getTemplateSamplesBudgetCodes(samples = []) {
      return [
        ...new Set(
          samples
            .map((sample) => this.getTemplateSampleBudgetCode(sample))
            .filter(Boolean)
        ),
      ];
    },
    getTemplateSampleBudgetValue(sample) {
      const budget =
        sample?.codPresupuesto || sample?.CodPresupuesto || null;
      if (!budget) return null;

      if (typeof budget === 'object') {
        const budgetCode = this.getTemplateSampleBudgetCode(sample);
        if (!budgetCode) return null;
        return {
          id: budgetCode,
          descripcion:
            this.normalizeString(
              budget?.descripcion || budget?.Descripcion
            ) || budgetCode,
        };
      }

      const budgetCode = this.getTemplateSampleBudgetCode(sample);
      return budgetCode
        ? {
            id: budgetCode,
            descripcion: budgetCode,
          }
        : null;
    },
    buildTemplateDefaultsForSamples(samples = []) {
      const normalizedSamples = Array.isArray(samples) ? samples : [];
      const budgetCodes =
        this.getTemplateSamplesBudgetCodes(normalizedSamples);

      if (budgetCodes.length !== 1) {
        return {};
      }

      const sharedBudget = this.getTemplateSampleBudgetValue(
        normalizedSamples[0]
      );
      return sharedBudget
        ? {
            codPresupuesto: sharedBudget,
          }
        : {};
    },
    getDiscountedPrice(offerLine) {
      const gross = Number(offerLine?.PrecioBruto);
      if (Number.isNaN(gross)) return null;

      const discount = Number(offerLine?.Descuento);
      const safeDiscount = Number.isNaN(discount) ? 0 : discount;

      return (
        Math.round((gross - (gross * safeDiscount) / 100) * 10) / 10
      );
    },
    mapTemplateSampleToRegisterPayload(
      sample,
      offerLinesByBudget = {},
      sampleIndex = 0
    ) {
      const description = this.getTemplateSampleDescription(sample);
      const sampleTypeId = this.getTemplateSampleTypeId(sample);
      const articleId = this.getTemplateSampleArticleId(sample);
      const sampleBudgetCode =
        this.getTemplateSampleBudgetCode(sample) ||
        Object.keys(offerLinesByBudget || {})[0] ||
        '';
      const offerLines = Array.isArray(offerLinesByBudget?.[sampleBudgetCode])
        ? offerLinesByBudget[sampleBudgetCode]
        : [];
      const offerLine = this.findOfferLineForSample(
        sample,
        offerLines
      );
      const familyId =
        this.getTemplateSampleFamilyId(sample) ||
        offerLine?.IdFamilia ||
        null;

      if (!description || !sampleTypeId || !articleId || !offerLine) {
        throw new Error(
          `template_missing_required_data_${sampleIndex + 1}`
        );
      }

      const automaticSamplingPoint = this.toBoolean(
        sample?.automaticSamplingPoint,
        true
      );
      const farmId = automaticSamplingPoint
        ? sample?.finca?.id || sample?.Parcelas || sample?.idFinca
        : null;
      const plotId = automaticSamplingPoint
        ? sample?.parcela?.id || sample?.Parcela || sample?.idParcela
        : null;
      const levelPointId =
        sample?.nivelPunto?.id ||
        sample?.idNivelPunto ||
        sample?.IdNivelPunto ||
        null;

      return {
        DescripcionMuestra: description,
        FamiliaTM: familyId,
        idTipoMuestra: sampleTypeId,
        Descripcion: {
          idTipoMuestra: sampleTypeId,
        },
        SubTipoMuestra:
          sample?.subTipoMuestra?.id ||
          sample?.idSubTipoMuestra ||
          sample?.SubTipoMuestra ||
          null,
        Articulo: articleId,
        CodPresupuesto: sampleBudgetCode || null,
        CodLinea: offerLine.CodLinea,
        idSubFamiliaTM:
          offerLine.IdSubFamilia || offerLine.IdSubFamiliaTM || null,
        PrecioAT: this.getDiscountedPrice(offerLine),
        Descuento: offerLine.Descuento ?? null,
        automaticSamplingPoint,
        Parcelas: farmId,
        FincaLiteral: automaticSamplingPoint
          ? null
          : this.normalizeString(
              sample?.fincaLiteral ||
                sample?.FincaLiteral ||
                sample?.finca?.descripcion
            ) || null,
        Parcela: plotId,
        ParcelaLiteral: automaticSamplingPoint
          ? null
          : this.normalizeString(
              sample?.parcelaLiteral ||
                sample?.ParcelaLiteral ||
                sample?.parcela?.descripcion
            ) || null,
        idNivelPunto: levelPointId || 0,
        cma: this.toBoolean(sample?.cma),
        lmr: this.toBoolean(sample?.lmr),
        trazaCultivo: this.toBoolean(sample?.trazaCultivo),
        CultivoOrganico: this.toBoolean(sample?.cultivoOrganico),
        Legislacion:
          sample?.legislacion?.id ||
          sample?.idLegislacion ||
          sample?.Legislacion ||
          null,
        Observaciones:
          this.normalizeString(
            sample?.observaciones || sample?.Observaciones
          ) || null,
        lote:
          this.normalizeString(sample?.lote || sample?.LoteCodigo) ||
          null,
        cloroResidual:
          sample?.cloroResidual || sample?.CloroResidualLibre || null,
      };
    },
    getTemplateSamplingDate(templateData) {
      const samplingDate =
        templateData?.defaults?.samplingDate ||
        templateData?.defaults?.SamplingDate ||
        null;
      if (!samplingDate) return new Date();

      const parsedDate = new Date(samplingDate);
      return Number.isNaN(parsedDate.getTime())
        ? new Date()
        : parsedDate;
    },
    getTemplateSampler(templateData) {
      const sampler =
        templateData?.defaults?.sampler ||
        templateData?.defaults?.Sampler ||
        null;
      return this.normalizeString(sampler) || null;
    },
    getTemplateClientCode(templateData) {
      return (
        this.normalizeString(
          templateData?.defaults?.codCliente ||
            templateData?.defaults?.CodCliente ||
            this.company
        ) || null
      );
    },
    async onRegisterTemplateClick() {
      const templateId = this.templateId;
      if (!templateId) return;

      let createdInvoiceId = null;
      let registrationRequested = false;

      try {
        this.$f7.preloader.show();

        const templateData = await this.fetchTemplate(templateId);
        const templateSamples = this.getTemplateSamples(templateData);
        const templateSampleBudgetCodes =
          this.getTemplateSamplesBudgetCodes(templateSamples);
        const budgetCode =
          this.getTemplateBudgetCode(templateData) ||
          templateSampleBudgetCodes[0] ||
          '';
        const registerBudgetCode =
          this.getTemplateSampleBudgetCode(
            templateSamples[templateSamples.length - 1]
          ) ||
          budgetCode;
        const sampleBudgetCodes = templateSampleBudgetCodes.length
            ? templateSampleBudgetCodes
            : budgetCode
            ? [budgetCode]
            : [];

        if (!templateSamples.length || !sampleBudgetCodes.length) {
          throw new Error('template_missing_required_data');
        }

        const offerLinesByBudget = {};
        for (const currentBudgetCode of sampleBudgetCodes) {
          await this.fetchDetallesPresupuestos(currentBudgetCode);
          const currentOfferLines = Array.isArray(
            this.detallesPresupuestos
          )
            ? [...this.detallesPresupuestos]
            : [];

          if (!currentOfferLines.length) {
            throw new Error('template_missing_required_data');
          }

          offerLinesByBudget[currentBudgetCode] = currentOfferLines;
        }

        await this.RegisterInvoice();

        const idInvoice = this.getCurrentInvoiceId();
        const codAlbaran = this.getCurrentInvoiceCode();
        if (!idInvoice || !codAlbaran) {
          throw new Error('invoice_not_created');
        }
        createdInvoiceId = idInvoice;

        const payloadSamples = templateSamples.map((sample, index) =>
          this.mapTemplateSampleToRegisterPayload(
            sample,
            offerLinesByBudget,
            index
          )
        );

        const codCliente = this.getTemplateClientCode(templateData);
        if (!codCliente) {
          throw new Error('template_missing_required_data');
        }

        this.setOfferSelected(registerBudgetCode);

        registrationRequested = true;
        await this.registerInvoiceSamples({
          isExpressMode: true,
          CodCliente: codCliente,
          idAlbaran: idInvoice,
          CodAlbaran: codAlbaran,
          CodPresupuesto: registerBudgetCode,
          Sampler: this.getTemplateSampler(templateData),
          SamplingDate: this.getTemplateSamplingDate(templateData),
          MuestrasExpress: payloadSamples,
        });

        await this.fetchInvoiceSamples(idInvoice);
        emitSamplesRegisterGa4Event(
          this,
          'sampling_template_load_success',
          this.getTemplateAnalyticsParams({
            screen_name: 'SamplesRegister',
            entry_point: 'template_details_load_button',
            register_mode: 'express',
            invoice_id: idInvoice || undefined,
            samples_count: payloadSamples.length,
            budget_code: registerBudgetCode || undefined,
          })
        );
        this.$f7.preloader.hide();
        this.setConfirmPopup(true);

        this.$f7router.navigate(
          {
            name: 'SamplesRegister',
            query: {
              tab: '0',
              mode: 'express',
              source: 'template',
            },
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        if (createdInvoiceId && !registrationRequested) {
          try {
            await this.deleteInvoice({
              idInvoice: createdInvoiceId,
              deleteSource: 'template_details_cleanup',
            });
          } catch (cleanupError) {
            console.warn(
              'Unable to rollback invoice created from template details',
              cleanupError
            );
          }
        }

        const missingFieldsError =
          String(error?.message || '').includes(
            'template_missing_required_data'
          ) ||
          String(error?.message || '').includes(
            'invoice_not_created'
          );

        this.$notifyDX(
          {
            message: missingFieldsError
              ? this.$t('samplesRegister.missingFieldsError')
              : this.$t(
                  this.$helpers.getFilteredErrorMessage(
                    error?.response || error?.message || error
                  )
                ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    mapTemplateSampleForDraft(sample, index) {
      return {
        ...sample,
        order: sample?.order || index + 1,
        descripcionMuestra:
          this.normalizeTemplateText(
            sample?.descripcionMuestra ?? sample?.DescripcionMuestra
          ) || '',
        observaciones:
          this.normalizeTemplateText(
            sample?.observaciones ?? sample?.Observaciones
          ) || '',
        cma: this.toBoolean(sample?.cma),
        lmr: this.toBoolean(sample?.lmr),
        trazaCultivo: this.toBoolean(sample?.trazaCultivo),
        cultivoOrganico: this.toBoolean(
          sample?.cultivoOrganico ?? sample?.CultivoOrganico
        ),
      };
    },
    buildTemplateSamplesPayload(samples = []) {
      const shortDescription =
        this.normalizeTemplateText(
          this.templateData?.shortDescription
        ) || '';
      const defaults = {
        ...(this.samplingTemplate?.defaults || {}),
        ...this.buildTemplateDefaultsForSamples(samples),
      };

      if (
        this.getTemplateSamplesBudgetCodes(samples).length !== 1
      ) {
        delete defaults.codPresupuesto;
        delete defaults.CodPresupuesto;
      }

      return {
        ...(this.samplingTemplate || {}),
        samples: samples.map((sample, index) =>
          this.mapTemplateSampleForDraft(sample, index)
        ),
        template: {
          name: this.templateData?.name || '',
          active: Boolean(this.templateData?.active),
          ...(shortDescription ? { shortDescription } : {}),
        },
        defaults,
      };
    },
    async onTemplateSamplesUpdated(samples) {
      if (this.savingTemplateSamples) return;

      if (!this.templateId) {
        this.$notifyDX(
          {
            message:
              this.$t('noDataText') !== 'noDataText'
                ? this.$t('noDataText')
                : 'No se pudo preparar la plantilla para editar',
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return;
      }

      try {
        this.savingTemplateSamples = true;
        this.$f7.preloader.show();

        await this.updateTemplate({
          id: this.templateId,
          data: this.buildTemplateSamplesPayload(samples),
        });
        await this.fetchTemplate(this.templateId);
        emitSamplesRegisterGa4Event(
          this,
          'sampling_template_samples_updated',
          this.getTemplateAnalyticsParams({
            screen_name: 'TemplateDetailsPage',
            entry_point: 'template_details_samples_grid',
            samples_count: Array.isArray(samples)
              ? samples.length
              : 0,
          })
        );

        this.$notifyDX(
          {
            message:
              this.$t('templateUpdatedSuccess') !==
              'templateUpdatedSuccess'
                ? this.$t('templateUpdatedSuccess')
                : this.$t('editSuccess') !== 'editSuccess'
                ? this.$t('editSuccess')
                : 'La plantilla se ha actualizado correctamente',
            width: 550,
          },
          'success',
          NOTIFY_TIME_LONG
        );
      } catch (error) {
        try {
          await this.fetchTemplate(this.templateId);
        } catch (fetchError) {
          console.warn(
            'Unable to refresh template after update error',
            fetchError
          );
        }

        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.savingTemplateSamples = false;
        this.$f7.preloader.hide();
      }
    },
    buildTemplateEditDraft() {
      const templateId = this.templateId;

      return {
        ...(this.samplingTemplate || {}),
        template: {
          ...(this.templateData || {}),
        },
        samples: this.templateSamples.map((sample, index) =>
          this.mapTemplateSampleForDraft(sample, index)
        ),
        sourceRouteName: 'TemplateDetailsPage',
        editingTemplateId: templateId,
      };
    },
    onEditTemplate() {
      const draft = this.buildTemplateEditDraft();

      if (!draft?.editingTemplateId) {
        this.$notifyDX(
          {
            message:
              this.$t('noDataText') !== 'noDataText'
                ? this.$t('noDataText')
                : 'No se pudo preparar la plantilla para editar',
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return;
      }

      emitSamplesRegisterGa4Event(
        this,
        'sampling_template_create_open',
        this.getTemplateAnalyticsParams({
          screen_name: 'TemplateCreatePage',
          entry_point: 'template_details_edit_button',
          template_mode: 'edit',
        })
      );
      this.setTemplateCreationDraft(draft);
      this.$f7router.navigate(
        {
          name: 'TemplateCreatePage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    formatTemplateDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';

      const format = this.$helpers.getDateFormat(this.company);
      const locale = format === 'MM/dd/yyyy' ? 'en-US' : 'es-ES';

      return date.toLocaleDateString(locale);
    },
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('samplingTemplatesModule', [
      'fetchTemplate',
      'updateTemplate',
      'setTemplateCreationDraft',
    ]),
    ...mapActions('Offers', ['fetchDetallesPresupuestos']),
    ...mapActions('samplesRegisterModule', [
      'RegisterInvoice',
      'registerInvoiceSamples',
      'fetchInvoiceSamples',
      'setConfirmPopup',
      'setOfferSelected',
      'deleteInvoice',
    ]),
  },
  watch: {
    loaded(value) {
      if (value) {
        this.scheduleSamplesGuidedTourStart();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-offer-card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(87, 84, 95, 0.3);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: clip;
}
</style>
